/**
 * Per-country data provenance: which official body publishes the rates a market
 * uses, where the published figures live, and when we last compared our numbers
 * against them.
 *
 * WHY THIS EXISTS: the site is a 46-market calculator with no published account
 * of where its numbers come from. An ad-network reviewer (and a reader) cannot
 * tell a sourced figure from a guessed one, which is exactly the "low value /
 * untrustworthy content" signal that gets a data site refused. Every entry here
 * points at a real, primary source that was fetched and compared figure-by-figure
 * against `server/tax-calculator.ts`.
 *
 * HONESTY RULE: never add an entry that has not actually been checked against the
 * URL it names, and never add a `checked` date for a check that did not happen.
 * A market with no entry is rendered as "not yet recorded" on purpose — a
 * documented gap is acceptable, an invented source is not.
 *
 * `checked` is the date the figures in the engine were last compared to the
 * source. It is NOT the date the tax rule changed.
 */
export interface TaxSource {
  /** ISO country code, matching the engine's country codes. */
  code: string;
  country: string;
  /** The body that publishes the rates. */
  authority: string;
  /** Short form for inline use. */
  authorityShort: string;
  /** The page the figures were read from. */
  url: string;
  /** Tax year the figures apply to. */
  taxYear: string;
  /** What part of the calculation this source covers. */
  scope: string;
  /** ISO date (YYYY-MM-DD) the engine's figures were last compared to the source. */
  checked: string;
}

const CHECKED = "2026-09-19";

export const TAX_SOURCES: Record<string, TaxSource> = {
  uk: {
    code: "uk",
    country: "United Kingdom",
    authority: "HM Revenue & Customs (HMRC)",
    authorityShort: "HMRC",
    url: "https://www.gov.uk/income-tax-rates",
    taxYear: "2026/27",
    scope:
      "Income tax bands and the personal allowance, plus National Insurance for employees from GOV.UK's own rates pages.",
    checked: CHECKED,
  },
  us: {
    code: "us",
    country: "United States",
    authority: "Internal Revenue Service (IRS)",
    authorityShort: "IRS",
    url: "https://www.irs.gov/newsroom/irs-releases-tax-inflation-adjustments-for-tax-year-2026-including-amendments-from-the-one-big-beautiful-bill",
    taxYear: "2026",
    scope:
      "Federal brackets and the standard deduction (Revenue Procedure 2025-32). Social Security and Medicare come from the Social Security Administration; each state's schedule comes from that state's own revenue department.",
    checked: CHECKED,
  },
  ca: {
    code: "ca",
    country: "Canada",
    authority: "Canada Revenue Agency (CRA)",
    authorityShort: "CRA",
    url: "https://www.canada.ca/en/revenue-agency/services/tax/individuals/tax-rates-brackets/current-year.html",
    taxYear: "2026",
    scope:
      "Federal brackets and the basic personal amount, plus CPP and EI from canada.ca's published 2026 rates. Provincial tax on this site is calculated on Ontario's schedule.",
    checked: CHECKED,
  },
  au: {
    code: "au",
    country: "Australia",
    authority: "Australian Taxation Office (ATO)",
    authorityShort: "ATO",
    url: "https://www.ato.gov.au/tax-rates-and-codes/tax-rates-australian-residents",
    taxYear: "2026-27",
    scope:
      "Resident income tax brackets and the Medicare levy from the ATO's published resident rates.",
    checked: CHECKED,
  },
  ie: {
    code: "ie",
    country: "Ireland",
    authority: "Revenue Commissioners (Revenue)",
    authorityShort: "Revenue",
    url: "https://www.revenue.ie/en/jobs-and-pensions/calculating-your-income-tax/tax-rate-band.aspx",
    taxYear: "2026",
    scope:
      "The single standard rate band (€44,000 for 2026), the 20% and 40% rates, personal tax credits and the Universal Social Charge.",
    checked: CHECKED,
  },
  de: {
    code: "de",
    country: "Germany",
    authority: "Bundesministerium der Finanzen (BMF) / §32a EStG",
    authorityShort: "BMF",
    url: "https://www.gesetze-im-internet.de/estg/__32a.html",
    taxYear: "2026",
    scope:
      "The §32a Einkommensteuergesetz tariff for Veranlagungszeitraum 2026 (the official statute text) and the BMF's Programmablaufplan 2026; social-insurance contribution ceilings from the 2026 Rechengrößen.",
    checked: CHECKED,
  },
  fr: {
    code: "fr",
    country: "France",
    authority: "Direction générale des finances publiques (DGFiP)",
    authorityShort: "DGFiP",
    url: "https://www.service-public.fr/particuliers/vosdroits/F1419",
    taxYear: "2026",
    scope:
      "The income tax barème (bracket thresholds and rates) published for the 2026 tax calculation, and the décote.",
    checked: CHECKED,
  },
  es: {
    code: "es",
    country: "Spain",
    authority: "Agencia Estatal de Administración Tributaria (AEAT) / Seguridad Social",
    authorityShort: "AEAT",
    url: "https://sede.agenciatributaria.gob.es/Sede/Retenciones.shtml",
    taxYear: "2026",
    scope:
      "The AEAT's 2026 withholding algorithm for employment income (the article 20 reduction and the state-plus-regional scale) and Seguridad Social contribution rates for 2026.",
    checked: CHECKED,
  },
  nl: {
    code: "nl",
    country: "Netherlands",
    authority: "Belastingdienst",
    authorityShort: "Belastingdienst",
    url: "https://www.belastingdienst.nl/wps/wcm/connect/bldcontentnl/belastingdienst/prive/inkomstenbelasting/heffingskortingen_boxen_tarieven/boxen_en_tarieven/box_1/box_1",
    taxYear: "2026",
    scope:
      "Box 1 brackets and rates for 2026 and the heffingskortingen (the general and labour tax credits).",
    checked: CHECKED,
  },
  in: {
    code: "in",
    country: "India",
    authority: "Income Tax Department, Central Board of Direct Taxes (CBDT)",
    authorityShort: "Income Tax Department",
    url: "https://www.incometax.gov.in/iec/foportal/",
    taxYear: "AY 2026-27",
    scope:
      "New-regime slab rates under section 115BAC, the ₹75,000 salary standard deduction, the section 87A rebate and the 4% health and education cess; the EPF employee contribution rate.",
    checked: CHECKED,
  },
};

/** Look up provenance for a market, or undefined when none has been recorded. */
export function getTaxSource(code: string): TaxSource | undefined {
  return TAX_SOURCES[code];
}

/** Date the provenance record itself was last reviewed (see legal.ts for the same rule). */
export const METHODOLOGY_LAST_UPDATED = "September 2026";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** "2026-09-19" -> "19 September 2026". Returns the input unchanged if it is not an ISO date. */
export function checkedLabel(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const monthIndex = Number(m[2]) - 1;
  if (monthIndex < 0 || monthIndex > 11) return iso;
  return `${Number(m[3])} ${MONTHS[monthIndex]} ${m[1]}`;
}

/** Countries that appear in the provenance table, in a stable display order. */
export const SOURCED_COUNTRY_CODES = [
  "uk",
  "us",
  "ca",
  "au",
  "ie",
  "de",
  "fr",
  "es",
  "nl",
  "in",
] as const;
